import { MenuItemText } from '@statsbomb/kitbag-components';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ViewConfigurationSource, ViewType } from '@/analytics/snowplow/types';
import { patchUserNavOverridesAtom } from '@/atoms/mutations/user/user';
import { unwrappedNavigationPlayerIdAtom } from '@/atoms/navigation';
import { unwrappedUserNavOverridesAtom } from '@/atoms/queries/user';
import { toastDataAtom } from '@/atoms/toast';
import { usePlayerNavItems } from '@/components/navigation/subNav/PlayerNav';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { PLAYER_BASE_ROUTE } from '@/utils/classicLinks';
import { useTrackExperienceChange } from '../../analytics/snowplow/hooks/useTrackExperienceChange';
import { usePlayerNavigationItems } from './subNavigation/usePlayerNavigationItems';

export const usePlayerNavigationItem = (crossPlatform = false) => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation('navigation');

  const navigate = useNavigate();
  const { datahubId, iqId } = useAtomValue(unwrappedNavigationPlayerIdAtom);
  const { mutateAsync: patchUserNavOverrides } = useAtomValue(patchUserNavOverridesAtom);
  const userNavOverrides = useAtomValue(unwrappedUserNavOverridesAtom);
  const setToastData = useSetAtom(toastDataAtom);
  const playerPath = `/${locale}/player/${datahubId}/`;

  const isTouch = useIsTouchDevice();

  const newXpPlayerSubnav = usePlayerNavItems(playerPath);
  const oldXpPlayerSubnav = usePlayerNavigationItems();

  const newXpDefaultPath = `${playerPath}dashboard`;
  const oldXpDefaultPath = `${PLAYER_BASE_ROUTE}/${iqId}`;

  const isClassicPlayerXp = userNavOverrides.player;
  const defaultPlayerPath = isClassicPlayerXp ? oldXpDefaultPath : newXpDefaultPath;
  const otherExperiencePath = isClassicPlayerXp ? newXpDefaultPath : oldXpDefaultPath;
  const playerSubnav = isClassicPlayerXp ? oldXpPlayerSubnav : newXpPlayerSubnav;
  const translationKey = isClassicPlayerXp ? 'newExperience' : 'classicExperience';
  const trackExperienceChange = useTrackExperienceChange(ViewType.PLAYER, ViewConfigurationSource.SIDE_NAVIGATION);

  const togglePlayerSectionXp = async () => {
    const isSaved = await patchUserNavOverrides({
      ...userNavOverrides,
      player: !isClassicPlayerXp,
    })
      .then(({ isSuccess }) => isSuccess)
      .catch(() => false);
    if (!isSaved) {
      setToastData({ toastName: 'switchExperience', type: 'error' });
      return;
    }
    // Tracking is best-effort: a failed or slow analytics call must not block the redirect.
    trackExperienceChange().catch(() => {});

    // handling the navigation here so we can await the patch request before redirecting
    if (otherExperiencePath.startsWith('https')) {
      window.location.href = otherExperiencePath;
    } else {
      navigate(otherExperiencePath);
    }
  };

  return {
    id: 'player',
    icon: 'PlayerSoccer' as const,
    path: defaultPlayerPath,
    label: t('primary.player'),
    subNav: [
      ...playerSubnav,
      ...(crossPlatform
        ? [
            {
              id: 'player-switch-experience',
              label: (
                <div>
                  <hr className="w-full absolute left-0 top-0 border-layering-1-strong" />
                  <MenuItemText isTouch={isTouch}>
                    <b>{t(`crossPlatform.secondary.${translationKey}`)}</b>
                  </MenuItemText>
                </div>
              ),
              onClick: togglePlayerSectionXp,
            },
          ]
        : []),
    ],
  };
};
